import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  IonItem,
  IonLabel,
  IonBadge,
  IonIcon,
  IonSpinner,
  IonAvatar,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { moonOutline } from 'ionicons/icons';
import { orderActorPresent } from '@crowdctrl/core';
import { OrderCopyItem } from '../../services/order.service';
import { formatDuration } from '../../utils/date.utils';

addIcons({
  moonOutline,
});

@Component({
  selector: 'crowdctrl-order-copy-item',
  standalone: true,
  imports: [CommonModule, IonItem, IonLabel, IonBadge, IonIcon, IonSpinner, IonAvatar],
  providers: [DatePipe],
  templateUrl: './order-copy-item.component.html',
  styleUrl: './order-copy-item.component.css',
})
export class OrderCopyItemComponent {
  private readonly datePipe = inject(DatePipe);

  @Input() order!: OrderCopyItem;
  @Input() button = true;
  @Input() detail = true;
  @Input() lines?: string;
  @Input() minHeight: string | null = null;
  @Input() selected = false;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() syncPending = false;
  @Input() showWaitTime = true;
  /** My Worklist variant: customer -> queue -> order number on heading row. */
  @Input() showCustomerFirstHeading = false;
  @Input() showPolicyVersion = true;
  @Input() showPriority = true;
  @Input() showEnqueuedAt = true;
  @Output() itemClick = new EventEmitter<OrderCopyItem>();

  onItemClick(): void {
    if (this.disabled || this.loading) return;
    this.itemClick.emit(this.order);
  }

  get isSnoozed(): boolean {
    if (!this.order?.snoozedUntil) return false;
    return new Date(this.order.snoozedUntil) > new Date();
  }

  get isBeingCalled(): boolean {
    return orderActorPresent(this.order?.callingBy);
  }

  /** Display label: session sequence number when present, else orderId/id. */
  get orderHeading(): string {
    if (this.order?.sessionSequence != null) {
      return String(this.order.sessionSequence);
    }
    return this.order?.orderId ?? this.order?.id ?? '—';
  }

  /** Customer / guest label for the heading row (trimmed). */
  get customerLabel(): string | null {
    const raw = this.order?.customerIdentifier?.trim();
    return raw ? raw : null;
  }

  get hasMetaLine(): boolean {
    return !!(
      this.order?.queueName?.trim() ||
      (this.showWaitTime && this.order?.waitTimeMs != null) ||
      this.policyVersionLabel ||
      this.appliedPriorityLabel
    );
  }

  get policyVersionLabel(): string | null {
    if (!this.showPolicyVersion) return null;
    const v = this.order?.policyVersion;
    if (typeof v === 'number' && Number.isFinite(v)) return `v${v}`;
    return null;
  }

  get appliedPriorityLabel(): string | null {
    if (!this.showPriority) return null;
    const pqp = this.order?.policyQueuePriority;
    if (typeof pqp === 'number' && Number.isFinite(pqp)) return `Priority ${pqp}`;

    const qp = this.order?.queuePriority;
    if (typeof qp === 'number' && Number.isFinite(qp)) return `Priority ${qp}`;
    return null;
  }

  formatWaitTime(ms: number): string {
    return formatDuration(ms);
  }

  /**
   * My Worklist only: smart enqueue display (seconds visible so policy ordering is easy to verify):
   * same local day as now → time with seconds (DatePipe `mediumTime`);
   * otherwise → short date + time with seconds.
   */
  get enqueuedAtSmartText(): string | null {
    if (!this.showCustomerFirstHeading) return null;
    const raw = this.order?.enqueuedAt;
    if (raw == null || (typeof raw === 'string' && !raw.trim())) return null;
    const ms = typeof raw === 'string' ? Date.parse(raw) : NaN;
    if (!Number.isFinite(ms)) return null;
    const enq = new Date(ms);
    const now = new Date();
    const sameLocalDay =
      enq.getFullYear() === now.getFullYear() &&
      enq.getMonth() === now.getMonth() &&
      enq.getDate() === now.getDate();
    const timeWithSeconds = this.datePipe.transform(enq, 'mediumTime');
    if (sameLocalDay) {
      return timeWithSeconds ?? null;
    }
    const datePart = this.datePipe.transform(enq, 'd MMM y');
    if (datePart && timeWithSeconds) {
      return `${datePart}, ${timeWithSeconds}`;
    }
    return this.datePipe.transform(enq, 'medium') ?? timeWithSeconds ?? null;
  }

  get showEnqueuedAtRow(): boolean {
    return this.showEnqueuedAt && this.enqueuedAtSmartText != null;
  }

  /**
   * Pending is the default queue state — no visible end badge; other statuses show a badge.
   */
  get showEndStatusBadge(): boolean {
    return !this.isPendingStatus(this.order?.status);
  }

  private isPendingStatus(status: string | undefined | null): boolean {
    return (
      String(status ?? '')
        .trim()
        .toLowerCase() === 'pending'
    );
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'danger';
      default:
        return 'medium';
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  }
}
